<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRentalFormFixedAssets',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    editable: {
      type: Boolean,
      required: false,
      default: true,
    },
    rentalId: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_2248226175642056_columns = computed(() => [
    {
      name: 'locked',
      type: 'lock',
    },
    {
      name: 'inactive',
      type: 'checkbox',
      header: $fn.value.gettext('Inactive'),
      width: '90px',
      textAlign: 'center',
    },
    {
      name: 'valid_from',
      type: 'date',
      header: $fn.value.gettext('Valid From'),
      width: '120px',
    },
    {
      name: 'valid_to',
      type: 'date',
      header: $fn.value.gettext('Valid To'),
      width: '120px',
    },
    {
      name: 'asset',
      type: 'select2',
      header: $fn.value.gettext('Fixed Asset'),
      width: '250px',
      required: true,
      path: 'name',
      bindObject: true,
      textField: 'name',
      options: $fn.value.searchDataSource('suggest_fixed_asset', {
        t: 'fixed_asset',
      }),
    },
    {
      name: 'notes',
      header: $fn.value.gettext('Notes'),
      width: '320px',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_items = () => {
    return $fn.value.fetch('list_rental_asset', {
      parameters: { rental_id: props.rentalId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { items: $$load_items },
    ux: { initialLoadSpinner: true },
    dataRefs: { items, ux, b_2248226175642056_columns },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.editable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editable');
    }
  );
  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );

  const act_2248226175642056_script_945v4xorv_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('rental_asset').upsert({
        ...$event.data.row,
        asset_id: $event.data.row.asset.id,
      })
    );
  };
  const act_2248226175642056_script_945v4xorv_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_945v4xorv_value_callable($event);
      },
    };
  };
  const act_2248226175642056_script_p564kkqk3_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('rental_asset').delete($event.data.row)
    );
  };
  const act_2248226175642056_script_p564kkqk3_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_p564kkqk3_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_35zoq9pud =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_bta0sx0ua =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_35zoq9pud,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_bta0sx0ua],
    event: 'reload',
  });
  const act_2248226175642056_script_945v4xorv = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_945v4xorv_getActionArgs,
  });
  const inst_evh_2248226175642056_save_2y59tp00s =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_945v4xorv,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_save_2y59tp00s],
    event: 'save',
  });
  const act_2248226175642056_script_p564kkqk3 = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_p564kkqk3_getActionArgs,
  });
  const inst_evh_2248226175642056_delete_pzz6burn7 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_p564kkqk3,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_delete_pzz6burn7],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_reload_bta0sx0ua,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_save_2y59tp00s,
    inst_evh_2248226175642056_save,
    inst_evh_2248226175642056_delete_pzz6burn7,
    inst_evh_2248226175642056_delete,
  };

  const evh_7315092382398562_reload_bta0sx0ua = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_bta0sx0ua.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_save_2y59tp00s = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_save_2y59tp00s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_save = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_delete_pzz6burn7 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_delete_pzz6burn7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_delete = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    loading-spinner
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataTable
      tableId="RentalFormFixedAssets"
      :editable="editable"
      :isRowEditable="(p) => !p.locked"
      :value="
        _.orderBy(
          items,
          ['created_at', 'valid_from', 'valid_to'],
          ['desc', 'desc', 'desc']
        )
      "
      :rowTemplate="
        schemaDefaults('CreateRentalAsset', {
          rental_id: rentalId,
          valid_from: new Date(),
        })
      "
      :columns="b_2248226175642056_columns"
      class="h-100"
      v-if="loaded"
      @save="evh_2248226175642056_save($event, {})"
      @delete="evh_2248226175642056_delete($event, {})"
    >
    </ItpDataTable>
  </ItpUsercontrol>
</template>
